import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useQuoteCart } from "../../contexts/QuoteCartContext";
import { Trash2, Plus, Minus, CheckCircle, X, Send, ArrowLeft, ShoppingBag } from "lucide-react";
import { t } from "../../i18n";
import { getProductName, translateSubcategoryName } from "../../utils/translator";
import { trackGoogleAdsConversion } from "../../utils/googleAds";

const QuoteCartPage = () => {
    const { lang } = useLanguage();
    const navigate = useNavigate();
    const { cartItems, updateQuantity, removeFromCart, clearCart, totalUniqueItems } = useQuoteCart();
    
    const [status, setStatus] = useState(null); // 'sending', 'success', 'error'
    const [formData, setFormData] = useState({
        name: "",
        company: "",
        email: "",
        phone: "",
        message: ""
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus("sending");

        // Format the products list for the email message body
        let productsListText = "";
        cartItems.forEach((item, index) => {
            productsListText += `${index + 1}. Ürün Adı: ${getProductName(item.product, lang)}\n`;
            productsListText += `   Ürün Kodu (SKU): ${item.product.id}\n`;
            productsListText += `   Seri: ${translateSubcategoryName(item.product.series || "N/A", lang)}\n`;
            if (item.product.subcategory) {
                productsListText += `   Alt Kategori: ${translateSubcategoryName(item.product.subcategory, lang)}\n`;
            }
            productsListText += `   Adet: ${item.quantity}\n`;
            productsListText += `   -----------------------------------------\n`;
        });

        const emailMessage = `
MÜŞTERİ BİLGİLERİ:
-----------------------------------------
Adı Soyadı: ${formData.name}
Firma Adı: ${formData.company || "Belirtilmemiş"}
Telefon: ${formData.phone}
E-posta: ${formData.email}

TALEP EDİLEN ÜRÜNLER:
-----------------------------------------
${productsListText}

KULLANICI NOTU:
-----------------------------------------
${formData.message || "Not eklenmemiş."}
`;

        const submitData = new FormData();
        submitData.append("name", formData.name);
        submitData.append("email", formData.email);
        submitData.append("phone", formData.phone || "");
        submitData.append("company", formData.company || "");
        submitData.append("subject", `Fiyat Talebi - ${formData.name} (${formData.company || "Bireysel"})`);
        submitData.append("message", emailMessage);

        const endpoint = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
            ? "https://www.profinoks.com/send-mail.php"
            : "/send-mail.php";

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                body: submitData
            });

            const data = await response.json();

            if (data.success) {
                setStatus("success");
                trackGoogleAdsConversion('quote_submit', {
                    item_count: cartItems.length,
                    currency: 'TRY',
                    value: 100
                });
                clearCart();
                window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
                setStatus("error");
                alert(data.message || (lang === "tr" ? "Teklif talebi gönderilirken bir hata oluştu!" : "An error occurred while sending the quote request!"));
            }
        } catch (error) {
            console.error("Quote submission error:", error);
            setStatus("error");
            alert(lang === "tr" ? "Teklif talebi gönderilirken bir hata oluştu!" : "An error occurred while sending the quote request!");
        }
    };

    if (status === "success") {
        return (
            <div className="pt-[140px] bg-gray-50 min-h-screen pb-20 flex items-center justify-center">
                <div className="mx-auto max-w-lg px-4 text-center">
                    <div className="bg-white p-8 lg:p-12 rounded-3xl border border-gray-100 shadow-xl flex flex-col items-center">
                        <div className="bg-green-50 p-4 rounded-full text-green-600 mb-6 animate-bounce">
                            <CheckCircle size={48} />
                        </div>
                        <h1 className="text-2xl lg:text-3xl font-extrabold text-gray-900 mb-4 tracking-wider uppercase">
                            {lang === "tr" ? "TALEBİNİZ ALINDI!" : "REQUEST RECEIVED!"}
                        </h1>
                        <p className="text-gray-600 text-sm leading-relaxed mb-8">
                            {lang === "tr" 
                                ? "Fiyat talebiniz başarıyla info@profinoks.net mail adresine iletildi. En kısa sürede tarafınızla iletişime geçilecektir." 
                                : "Your price request has been successfully sent to info@profinoks.net. We will contact you as soon as possible."}
                        </p>
                        <Link 
                            to="/products"
                            className="w-full bg-primary hover:bg-primary/95 text-white font-bold py-4 rounded-xl transition-colors tracking-widest text-xs uppercase"
                        >
                            {lang === "tr" ? "ÜRÜNLERE GERİ DÖN" : "BACK TO PRODUCTS"}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-[140px] bg-gray-50 min-h-screen pb-20">
            {/* Breadcrumbs */}
            <div className="bg-white border-b border-gray-100 py-4 mb-10">
                <div className="mx-auto max-w-7xl px-4 flex items-center space-x-2 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                    <Link to="/" className="hover:text-primary transition-colors">
                        {lang === "tr" ? "ANASAYFA" : "HOME"}
                    </Link>
                    <ChevronRightIcon />
                    <Link to="/products" className="hover:text-primary transition-colors">
                        {lang === "tr" ? "ÜRÜNLER" : "PRODUCTS"}
                    </Link>
                    <ChevronRightIcon />
                    <span className="text-primary">{lang === "tr" ? "TEKLİF SEPETİM" : "QUOTE CART"}</span>
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-10">
                    <h1 className="text-3xl font-extrabold tracking-[0.1em] text-gray-900 uppercase">
                        {lang === "tr" ? "TEKLİF SEPETİM" : "QUOTE CART"}
                    </h1>
                    <div className="h-1 w-16 bg-primary mt-3"></div>
                </div>

                {totalUniqueItems === 0 ? (
                    <div className="bg-white rounded-3xl border border-gray-100 p-12 text-center shadow-sm max-w-2xl mx-auto flex flex-col items-center">
                        <div className="bg-gray-50 p-5 rounded-full text-gray-400 mb-6">
                            <ShoppingBag size={40} />
                        </div>
                        <h2 className="text-lg font-bold text-gray-800 uppercase tracking-widest mb-3">
                            {lang === "tr" ? "Sepetiniz Boş" : "Your Cart is Empty"}
                        </h2>
                        <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                            {lang === "tr"
                                ? "Teklif almak istediğiniz ürünleri ürün detay sayfalarındaki 'TEKLİF AL' butonunu kullanarak sepetinize ekleyebilirsiniz."
                                : "You can add products you wish to get quotes for using the 'GET A QUOTE' button on product detail pages."}
                        </p>
                        <Link 
                            to="/products"
                            className="bg-primary hover:bg-primary/95 text-white font-bold px-8 py-4 rounded-xl transition-colors tracking-widest text-xs uppercase inline-flex items-center space-x-2"
                        >
                            <ArrowLeft size={16} />
                            <span>{lang === "tr" ? "ÜRÜNLERİ İNCELE" : "BROWSE PRODUCTS"}</span>
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
                        {/* Cart Items List */}
                        <div className="lg:col-span-2 space-y-4">
                            {cartItems.map((item) => (
                                <div 
                                    key={item.product.id}
                                    className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-6"
                                >
                                    <div className="flex items-center gap-5 w-full sm:w-auto">
                                        <div className="h-20 w-20 shrink-0 bg-gray-50 rounded-xl border border-gray-100 overflow-hidden flex items-center justify-center p-2">
                                            <img 
                                                src={item.product.imageUrl} 
                                                alt={getProductName(item.product, lang)} 
                                                className="max-h-full max-w-full object-contain"
                                            />
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-bold text-primary uppercase tracking-wider block mb-1">
                                                {translateSubcategoryName(item.product.series || item.product.subcategory, lang)}
                                            </span>
                                            <h3 className="text-sm font-bold text-gray-900 leading-snug">
                                                {getProductName(item.product, lang)}
                                            </h3>
                                            <p className="text-gray-400 text-xs mt-1">
                                                SKU: {item.product.id}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-6 justify-between w-full sm:w-auto shrink-0">
                                        <div className="flex items-center bg-gray-50 rounded-xl p-1 border border-gray-100">
                                            <button 
                                                onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                                                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-primary transition-colors hover:bg-white rounded-lg"
                                            >
                                                <Minus size={14} />
                                            </button>
                                            <span className="w-8 text-center font-bold text-xs text-gray-900">{item.quantity}</span>
                                            <button 
                                                onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                                                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-primary transition-colors hover:bg-white rounded-lg"
                                            >
                                                <Plus size={14} />
                                            </button>
                                        </div>

                                        <button 
                                            onClick={() => removeFromCart(item.product.id)}
                                            className="text-gray-400 hover:text-red-500 p-2 transition-colors"
                                            title={lang === "tr" ? "Sil" : "Remove"}
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            
                            <div className="flex justify-between items-center pt-2">
                                <Link 
                                    to="/products"
                                    className="text-primary hover:text-primary/80 text-xs font-bold uppercase tracking-widest flex items-center space-x-2"
                                >
                                    <ArrowLeft size={14} />
                                    <span>{lang === "tr" ? "ÜRÜN EKLEMEYE DEVAM ET" : "CONTINUE ADDING PRODUCTS"}</span>
                                </Link>
                                
                                <button 
                                    onClick={clearCart}
                                    className="text-gray-400 hover:text-red-500 text-xs font-bold uppercase tracking-widest"
                                >
                                    {lang === "tr" ? "SEPETİ TEMİZLE" : "CLEAR CART"}
                                </button>
                            </div>
                        </div>

                        {/* Request Form */}
                        <div className="bg-white p-6 lg:p-8 rounded-3xl border border-gray-100 shadow-sm">
                            <h2 className="text-lg font-bold text-gray-950 uppercase tracking-widest mb-6 border-l-4 border-primary pl-3">
                                {lang === "tr" ? "TEKLİF TALEBİ FORMU" : "QUOTE REQUEST FORM"}
                            </h2>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 font-bold">
                                        {lang === "tr" ? "ADINIZ SOYADINIZ" : "FULL NAME"} *
                                    </label>
                                    <input 
                                        name="name"
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-primary transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 font-bold">
                                        {lang === "tr" ? "FİRMA ADI" : "COMPANY NAME"}
                                    </label>
                                    <input 
                                        name="company"
                                        type="text"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-primary transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 font-bold">
                                        {lang === "tr" ? "TELEFON NUMARASI" : "PHONE NUMBER"} *
                                    </label>
                                    <input 
                                        name="phone"
                                        type="tel"
                                        required
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-primary transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 font-bold">
                                        {lang === "tr" ? "E-POSTA ADRESİ" : "EMAIL ADDRESS"} *
                                    </label>
                                    <input 
                                        name="email"
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-primary transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 font-bold">
                                        {lang === "tr" ? "NOTLAR / ÖZEL TALEPLER" : "NOTES / SPECIAL REQUESTS"}
                                    </label>
                                    <textarea 
                                        name="message"
                                        rows="4"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-primary transition-colors resize-none"
                                    ></textarea>
                                </div>

                                <button 
                                    type="submit"
                                    disabled={status === "sending"}
                                    className="w-full bg-primary hover:bg-primary/95 disabled:bg-primary/50 text-white text-xs font-bold uppercase tracking-[0.2em] py-4 rounded-xl transition-all flex items-center justify-center space-x-2 mt-6 shadow-lg shadow-primary/20 hover:-translate-y-0.5 active:translate-y-0 transform duration-200"
                                >
                                    {status === "sending" ? (
                                        <>
                                            <div className="h-4 w-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                            <span>{lang === "tr" ? "GÖNDERİLİYOR..." : "SENDING..."}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Send size={14} />
                                            <span>{lang === "tr" ? "TEKLİF TALEBİ GÖNDER" : "SEND QUOTE REQUEST"}</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const ChevronRightIcon = () => (
    <svg className="text-gray-300 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
);

export default QuoteCartPage;
